import { useState, useEffect } from 'react';
import { getProjects, createProject, updateProject, deleteProject } from '../api';
import { usePreferences } from '../context/PreferencesContext';

export default function ProjectsDashboard({ onSelectProject, onProjectsLoaded, showAddNew, onAddNewHandled }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [error, setError] = useState('');
  const { viewMode } = usePreferences();

  const load = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
      onProjectsLoaded?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (showAddNew) { setShowAdd(true); onAddNewHandled?.(); }
  }, [showAddNew]);

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.name}"? This will not delete the players inside it.`)) return;
    await deleteProject(project.projectId);
    load();
  };

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="page-header">
        <div>
          <h2>Projects</h2>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Select a project to manage players and budgets</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ New Project</button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : projects.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
          <h3 style={{ marginBottom: 8, color: 'var(--text)' }}>No projects yet</h3>
          <p style={{ color: 'var(--muted)', marginBottom: 24 }}>Create your first project to start tracking players and payments.</p>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>Create First Project</button>
        </div>
      ) : viewMode === 'list' ? (
        <table className="list-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Season</th>
              <th>Venmo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <tr key={p.projectId} style={{ cursor: 'pointer' }}>
                <td onClick={() => onSelectProject(p)}>
                  <div style={{ fontWeight: 700 }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.description}</div>}
                </td>
                <td style={{ color: 'var(--muted)' }}>{p.season || '—'}</td>
                <td style={{ color: 'var(--muted)' }}>{p.venmoUsername ? `@${p.venmoUsername}` : '—'}</td>
                <td>
                  <div className="actions">
                    <button className="btn btn-xs btn-ghost" onClick={() => onSelectProject(p)}>Open</button>
                    <button className="btn btn-xs btn-ghost" onClick={() => setEditingProject(p)}>Edit</button>
                    <button className="btn btn-xs" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--red)' }} onClick={() => handleDelete(p)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="project-grid">
          {projects.map(p => (
            <ProjectCard key={p.projectId} project={p}
              onClick={() => onSelectProject(p)}
              onEdit={() => setEditingProject(p)}
              onDelete={() => handleDelete(p)} />
          ))}
        </div>
      )}

      {showAdd && <ProjectModal onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); }} />}
      {editingProject && <ProjectModal existing={editingProject} onClose={() => setEditingProject(null)} onSave={() => { setEditingProject(null); load(); }} />}
    </div>
  );
}

function ProjectCard({ project, onClick, onEdit, onDelete }) {
  const created = new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <div className="project-card" onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>📁</div>
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-xs btn-ghost" onClick={onEdit}>Edit</button>
          <button className="btn btn-xs" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--red)' }} onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div className="project-name">{project.name}</div>
      {project.season && <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600, marginBottom: 4 }}>{project.season}</div>}
      {project.description && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6, lineHeight: 1.5 }}>{project.description}</div>}
      {project.venmoUsername && <div style={{ fontSize: 12, color: 'var(--navy)', marginBottom: 4 }}>💸 @{project.venmoUsername}</div>}
      <div style={{ fontSize: 11, color: 'var(--muted)', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10 }}>Created {created}</div>
    </div>
  );
}

function ProjectModal({ existing, onClose, onSave }) {
  const [form, setForm] = useState({
    name: existing?.name || '',
    season: existing?.season || '',
    description: existing?.description || '',
    venmoUsername: existing?.venmoUsername || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (existing) { await updateProject(existing.projectId, form); }
      else { await createProject(form); }
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{existing ? 'Edit Project' : 'New Project'}</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Project Name</label>
            <input value={form.name} onChange={set('name')} required autoFocus placeholder="e.g. OL Red 2016, Spring Fundraiser" />
          </div>
          <div className="form-group">
            <label>Season / Year (optional)</label>
            <input value={form.season} onChange={set('season')} placeholder="e.g. 2025-26" />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <textarea value={form.description} onChange={set('description')} rows={2} placeholder="Brief description…" />
          </div>
          <div className="form-group">
            <label>Venmo Username (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
              <span style={{ padding: '9px 10px', background: 'var(--surface-2)', color: 'var(--muted)', fontSize: 14, borderRight: '1px solid var(--border)' }}>@</span>
              <input value={form.venmoUsername}
                onChange={e => setForm(f => ({ ...f, venmoUsername: e.target.value.replace('@', '') }))}
                placeholder="your-venmo-username"
                style={{ border: 'none', borderRadius: 0, flex: 1 }} />
            </div>
            <div className="helper-text">Parents see a "Pay with Venmo" button on their balance page.</div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : existing ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
